// Maps a live core gameState to the music-controller's audio inputs (intensity,
// countdown, crash / pickup / swap counts). Shared by local play (LocalGame) and
// online play (RemoteGame) so both get the same countdown beeps, crash sound and
// adaptive music. See music-controller.ts for how these inputs drive the audio.
//
//  - intensity (0..1): calm anticipation during the start countdown, then rising
//    tension as players are eliminated (peaking when only one worm remains).
//  - countdownLeft: seconds until the round starts, driving the 3-2-1-GO beeps.
//  - crashes / pickups: cumulative counts of "worm_died" game events (one per worm
//    wipeout, so a twin clone crash still counts) and power-up "despawn" (pickup)
//    events, driving the crash / pickup SFX.
//  - switches: cumulative switcharoonie pickups, driving the distinct swap SFX
//    (these are excluded from `pickups` so they don't also play the pickup arpeggio).
// Returns null when no game is live so the engine can reset between rounds.

import * as gsf from "core/src/core/game-state-functions.js";
import { SWITCH_ANIM_DURATION } from "core/src/core/constants.js";
import type { GameAudioState } from "./music-controller";

// `localIds`: this device's players, whose crashes get the heavier own-crash sound.
export function computeGameAudio(gameState: any, localIds: readonly any[] = []): GameAudioState | null {
  if (!gameState) {
    return null;
  }
  const countdownLeft = gsf.getStartPhaseTimeLeft(gameState);
  // The beeps only run through the countdown proper, after the roll call, and
  // never from higher than 3 (a longer first countdown, see Countdown.tsx, is
  // silence until its last three seconds).
  const countdownLength = Math.min(gsf.getCountdownLength(gameState), 3);
  let intensity;
  if (gsf.isInStartPhase(gameState)) {
    intensity = 0.15;
  } else {
    const total = gameState.players.length;
    const alive = gsf.getAlivePlayers(gameState).length;
    if (total <= 1) {
      intensity = 0.6;
    } else {
      const eliminatedFraction = (total - alive) / (total - 1);
      intensity = 0.4 + 0.55 * eliminatedFraction;
    }
  }
  // Crash SFX fires per WORM wipeout ("worm_died"), not per player elimination
  // ("player_died"): with a twin a player owns two worms, and a clone crashing
  // should still thud even though the player plays on. A server that predates the
  // per-worm event only emits "player_died" (one per player), so fall back to that
  // when no "worm_died" events are present - this keeps online crashes audible when
  // a newer client (which counts worm_died) talks to an older server, e.g. a preview
  // build against production. Once both events arrive we count worm_died only, so a
  // last-worm crash (which emits both) still sounds exactly once.
  // A power-up despawn only happens on pickup. The despawn event carries just the
  // id, so resolve the power-up name from its earlier spawn event to single out
  // switcharoonies (which get their own swap SFX instead of the pickup arpeggio).
  // Counted incrementally (this runs every frame and the event lists only grow
  // during a round): see countEvents.
  const c = countEvents(gameState, localIds);
  const crashes = c.wormDeaths || c.playerDeaths;
  const pickups = c.pickups;
  const switches = c.switches;
  const pickupsGreen = c.green;
  const pickupsRed = c.red;
  const spawns = c.spawns;
  const ownCrashes = c.ownDeaths;
  const missileHits = c.missileHits;
  // Switcharoonie portal swoosh (gameState.switchAnim: set by the online switch_freeze/switch_go
  // handlers, and by core itself in local play). A marker, not a count: portalOpenAt carries the
  // game-time the current sequence's portals open, 0 otherwise. The music-controller plays the
  // riser when the marker CHANGES to a new nonzero value, so a sequence sounds exactly once and
  // back-to-back switches (distinct timestamps) each sound. Deliberately the sequence's ONLY
  // sound: an arrival pop at go read as "something else happened".
  let portalOpenAt = 0;
  const anim = gameState.switchAnim;
  if (anim && anim.goTime != null) {
    const t = gameState.gameTime || 0;
    const animStart = Math.max(anim.freezeTime || 0, anim.goTime - SWITCH_ANIM_DURATION);
    if (t >= animStart) portalOpenAt = animStart;
  }
  return { intensity, countdownLeft, countdownLength, crashes, ownCrashes, pickups, pickupsGreen, pickupsRed, spawns, missileHits, switches, portalOpenAt };
}

// Running event counts per gameState, advanced by cursor over the append-only event
// lists. A list that was replaced or got shorter (a new round, a replay seek) is
// recounted from the start.
interface EventCounts {
  gameEvents: any[] | null; gi: number; wormDeaths: number; playerDeaths: number; ownDeaths: number;
  effectEvents: any[] | null; ei: number; missileHits: number; redNames: Set<string>;
  powerUpEvents: any[] | null; pi: number; names: Record<string, string>; affects: Record<string, string>;
  pickups: number; green: number; red: number; spawns: number; switches: number;
}
const counts = new WeakMap<object, EventCounts>();
function countEvents(gameState: any, localIds: readonly any[]): EventCounts {
  let c = counts.get(gameState);
  if (!c) {
    c = { gameEvents: null, gi: 0, wormDeaths: 0, playerDeaths: 0, ownDeaths: 0, effectEvents: null, ei: 0, missileHits: 0, redNames: new Set(), powerUpEvents: null, pi: 0, names: {}, affects: {}, pickups: 0, green: 0, red: 0, spawns: 0, switches: 0 };
    counts.set(gameState, c);
  }
  const ge = gameState.gameEvents || [];
  if (ge !== c.gameEvents || ge.length < c.gi) {
    c.gameEvents = ge; c.gi = 0; c.wormDeaths = 0; c.playerDeaths = 0; c.ownDeaths = 0;
  }
  for (; c.gi < ge.length; c.gi++) {
    const e = ge[c.gi];
    if (e.type === "worm_died") {
      c.wormDeaths++;
      if (localIds.includes(e.playerId)) c.ownDeaths++;
    }
    else if (e.type === "player_died") c.playerDeaths++;
  }
  const pe = gameState.powerUpEvents || [];
  if (pe !== c.powerUpEvents || pe.length < c.pi) {
    c.powerUpEvents = pe; c.pi = 0; c.names = {}; c.affects = {}; c.pickups = 0; c.green = 0; c.red = 0; c.spawns = 0; c.switches = 0;
  }
  for (; c.pi < pe.length; c.pi++) {
    const e = pe[c.pi];
    if (e.type === "spawn") {
      c.names[e.powerUp.id] = e.powerUp.name;
      c.affects[e.powerUp.id] = e.powerUp.affects;
      if (e.powerUp.affects === "others") c.redNames.add(e.powerUp.name);
      c.spawns++;
    } else if (e.type === "despawn") {
      if (c.names[e.id] === "switcharoonie") c.switches++;
      else {
        c.pickups++;
        if (c.affects[e.id] === "self") c.green++;
        else if (c.affects[e.id] === "others") c.red++;
      }
    }
  }
  // A missile landing: a red power-up's effect taking hold on a rival (core adds
  // the effect when the missile catches its worm; one per rival hit).
  const ee = gameState.effectEvents || [];
  if (ee !== c.effectEvents || ee.length < c.ei) {
    c.effectEvents = ee; c.ei = 0; c.missileHits = 0;
  }
  for (; c.ei < ee.length; c.ei++) {
    const e = ee[c.ei];
    if (e.type === "spawn" && e.effect && c.redNames.has(e.effect.name)) c.missileHits++;
  }
  return c;
}
